import * as Sentry from "@sentry/react";
import { Component, Fragment, ReactNode } from "react";

import { Button } from "antd";
import PropTypes from "prop-types";
import styled from "styled-components";

const title = "We're sorry, it looks like we're having issues.";
const buttonLabel = "Click to Retry";

interface ErrorBoundaryState {
  error: string;
  errorInfo: string;
  hasError: boolean;
  /** Incremented on retry to force a full remount of the child subtree (FR-007). */
  resetKey: number;
}

export default class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    error: "",
    errorInfo: "",
    hasError: false,
    resetKey: 0
  };
  static propTypes: { children: PropTypes.Validator<unknown> };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Send error to Sentry with additional context
    Sentry.withScope((scope) => {
      // Add error boundary context
      scope.setTag("errorBoundary", true);
      scope.setLevel("error");

      // Add component stack trace information
      if (errorInfo?.componentStack) {
        scope.setContext("errorInfo", {
          componentStack: errorInfo.componentStack
        });
      }

      // Add any additional error details
      if (errorInfo) {
        scope.setExtra("errorInfo", errorInfo);
      }

      // Capture the exception
      Sentry.captureException(error);
    });
  }
  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <ErrorBoundaryRoot>
          <h5>{title}</h5>
          <Button
            type="primary"
            onClick={() =>
              this.setState((prev) => ({
                hasError: false,
                error: "",
                errorInfo: "",
                // Bump resetKey so the keyed wrapper forces a fresh child subtree mount.
                resetKey: prev.resetKey + 1
              }))
            }>
            {buttonLabel}
          </Button>
        </ErrorBoundaryRoot>
      );
    }
    // Keyed Fragment: when resetKey increments on retry, React unmounts and
    // remounts the entire child subtree so a previously-throwing child gets a
    // clean first render rather than immediately re-throwing (FR-007).
    return <Fragment key={this.state.resetKey}>{this.props.children}</Fragment>;
  }
}

const ErrorBoundaryRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
